import { axios_instance } from "@/api/api";

export interface ReportFinding {
  check: string;
  level: "block" | "warn";
  message: string;
}

export interface ReportIntegrity {
  score: number;
  blocked: boolean;
  findings?: ReportFinding[];
}

export interface ReportFilters {
  campaign_id: string;
  from?: number;
  to?: number;
  data_type?: string;
  agent_id?: string;
  search?: string;
}
export type ReportFormat = "pdf" | "csv" | "json";
export interface ReportCount {
  key: string;
  count: number;
}
export interface ReportPreview {
  summary: ReportCount[];
  trend: ReportCount[];
  agents: ReportCount[];
  total_records: number;
  total_agents: number;
  records: {
    id: string;
    data_type: string;
    agent_id: string;
    created: number;
    source: Record<string, unknown>;
  }[];
  total_filter: number;
  types: { key: string; label: string }[];
}
export async function previewCampaignReport(
  filters: ReportFilters,
  page = 1,
  size = 20,
): Promise<ReportPreview> {
  const response = await axios_instance.post<{
    message: string;
    data: ReportPreview;
  }>("/api/v1/view/report/preview", { ...filters, page, size });
  return response.data.data;
}

// Subset of model.ExportTask that the report UI consumes.
export interface ExportTask {
  id: string;
  type: string;
  format?: string;
  report_filters?: ReportFilters;
  campaign_id?: string;
  status: string;
  file_path?: string;
  total_records?: number;
  exported_records: number;
  progress_percent: number;
  error?: string;
  integrity?: ReportIntegrity | null;
  attempts: number;
  created: number;
  modified: number;
  completed?: number;
}

interface GenerateReportResponse {
  message: string;
  data: { id: string };
}

interface TaskResponse {
  message: string;
  data: ExportTask;
}

// Generation permissions remain enforced by the report endpoint.
export async function generateCampaignReport(
  filters: ReportFilters,
  format: ReportFormat = "pdf",
): Promise<GenerateReportResponse> {
  const response = await axios_instance.post<GenerateReportResponse>(
    "/api/v1/view/report",
    { ...filters, format },
  );
  return response.data;
}

// List the report tasks already created for a campaign (newest first).
export async function listCampaignReports(
  campaignId: string,
  page = 1,
  size = 20,
): Promise<{ data: ExportTask[] | null; total_filter: number }> {
  const response = await axios_instance.post<{
    message: string;
    data: ExportTask[] | null;
    total_filter: number;
  }>("/api/v1/view/report/pagination", {
    campaign_id: campaignId,
    page,
    size,
  });
  return response.data;
}

// Poll a report/export task by id (generic browser-history export route).
export async function getExportTask(taskId: string): Promise<ExportTask> {
  const response = await axios_instance.get<TaskResponse>(
    `/api/v1/view/browser-history/export/${taskId}`,
  );
  return response.data.data;
}
